/*
 * Deskon - tells the CSS whether the header bar is actually filled or actually transparent.
 *
 * ------------------------------------------------------------------------------------------------
 * WHY THIS EXISTS AT ALL: NO CLASS IN THE DOM ANSWERS THE QUESTION.
 *
 * Three attempts were made to detect the transparent header from markup alone, and all three
 * failed for the same reason - the classes describe CONFIGURATION, not STATE:
 *
 *   header-component.color-scheme-6            present in both states
 *   --transparent-header-offset-boolean: 1     read 1 in both states at the moment it was checked
 *   .header__row--top.color-scheme-deskon2-white
 *                                              present at scrollY 0, over the hero, while the
 *                                              row's background was rgba(0, 0, 0, 0)
 *
 * That last one is the clearest: the row carries the WHITE scheme class while it is transparent.
 * The class says which palette the row would use if it were filled, not whether it is.
 *
 * THE ONLY THING THAT ACTUALLY CHANGES IS THE COMPUTED BACKGROUND COLOUR - rgba(0,0,0,0) over the
 * hero, rgb(255,255,255) once scrolled - and CSS cannot select on a computed value. So it is read
 * here and published as an attribute the stylesheet can use.
 *
 * THE SCRIPT REPORTS A FACT, THE STYLESHEET DECIDES THE LOOK. Nothing here sets a colour. If the
 * icons ever need a different pair of colours, that is a change in
 * assets/deskon-header-icons.css alone.
 * ------------------------------------------------------------------------------------------------
 * TWO REASONS THE ICONS USED TO STAY BLACK FOR A SECOND OR TWO AFTER SCROLLING BACK TO THE TOP.
 * Both are about WHEN the background is read, not about what is read. They are worth keeping in
 * mind before adding any new event to the list below, because either one alone brings the lag back.
 *
 *   1. NOTHING FIRES AFTER THE SCROLL STOPS. The old version listened to `scroll` and re-read once
 *      per frame while it was firing. But the header changes state at the END of the gesture - the
 *      last scroll event is dispatched, momentum finishes, and the bar becomes transparent AFTER
 *      that, with no event left to notice it. The state then sat stale until something unrelated
 *      happened to move in the page and woke the MutationObserver. That is the "one or two
 *      seconds": it was not a computation, it was a wait for a coincidence.
 *
 *      THE FIX IS TO KEEP WATCHING FOR A MOMENT AFTER THE LAST EVENT rather than to add more
 *      events. watch() re-reads every frame until the answer has held still for SETTLE_MS, so the
 *      change is caught whether it lands during the gesture, at its end, or a few frames later.
 *
 *   2. THE BACKGROUND IS ANIMATED, SO THE CURRENT VALUE IS NOT THE ANSWER. The row fades between
 *      opaque and transparent. Read mid-fade, its alpha is still above the threshold, so the bar
 *      reads as "filled" for the whole length of the fade even once the decision has been made.
 *
 *      THE FIX IS TO READ WHERE THE COLOUR IS GOING, NOT WHERE IT IS. A running CSS transition is
 *      an Animation object: getAnimations() hands back its keyframes, and the last one holds the
 *      TARGET background-color. Reading that flips the icons on the first frame of the fade
 *      instead of the last. targetBackground() below returns it, and falls back to the computed
 *      value whenever no transition is running - which is the normal, settled case.
 * ------------------------------------------------------------------------------------------------
 *
 * ALPHA IS THE TEST, NOT THE COLOUR. Checking for white would break the day the header is given a
 * dark or tinted fill; checking that the background is not fully transparent holds whatever colour
 * it is. Anything above a hair of opacity counts as filled - a background at 0.02 alpha is not
 * something an icon can be read against.
 *
 * THE ATTRIBUTE GOES ON <html>, not on the row. The row is inside a section that Shopify re-renders
 * - adding to the cart replaces the header markup, which is exactly how the wishlist badge got
 * lost - and an attribute written onto replaced markup goes with it. <html> is never re-rendered.
 *
 * A MutationObserver CATCHES THOSE RE-RENDERS. After a section swap the row element is a new node
 * with a possibly different background, and no scroll event follows. The handler is idempotent -
 * it only writes when the value changes - so it cannot loop against its own mutation, and the
 * observer watches document.body while the attribute is written on <html>.
 *
 * THE TRANSITION EVENTS ARE BOUND TO document, NOT TO THE ROW, for the same re-render reason: a
 * listener attached to the row dies with it. They bubble, so one listener on the document catches
 * every row that ever exists.
 */

(function () {
  'use strict';

  var ROW = '.header__row--top';
  var ATTR = 'data-dk-header';

  /* How long the answer has to hold still before watch() stops re-reading. Long enough to outlast
     a scroll-momentum tail and a background fade, short enough to be over before the next gesture. */
  var SETTLE_MS = 1200;

  function row() {
    return document.querySelector(ROW);
  }

  function alphaOf(colour) {
    if (!colour || colour === 'transparent') return 0;
    var parts = colour.match(/[\d.]+/g);
    if (!parts) return 0;
    return parts.length > 3 ? parseFloat(parts[3]) : 1;
  }

  /*
   * The colour the background is HEADING FOR while a transition is running on it, or null when
   * none is - see note 2 at the top. Everything here is guarded: getAnimations and getKeyframes
   * are the kind of API that throws rather than returns null in odd states, and a header that
   * cannot be read must fall back to the computed value, never break the page.
   */
  function targetBackground(el) {
    if (!el.getAnimations) return null;

    var animations;
    try {
      animations = el.getAnimations();
    } catch (e) {
      return null;
    }

    for (var i = 0; i < animations.length; i++) {
      var animation = animations[i];
      if (animation.transitionProperty !== 'background-color') continue;
      if (!animation.effect || !animation.effect.getKeyframes) continue;

      var frames;
      try {
        frames = animation.effect.getKeyframes();
      } catch (e) {
        continue;
      }

      var last = frames[frames.length - 1];
      if (last && last.backgroundColor) return last.backgroundColor;
    }

    return null;
  }

  function isFilled(el) {
    var colour = targetBackground(el) || getComputedStyle(el).backgroundColor;
    return alphaOf(colour) > 0.05;
  }

  function read() {
    var el = row();
    if (!el) return null;

    var value = isFilled(el) ? 'filled' : 'clear';
    var root = document.documentElement;

    /* Guarded: an unconditional write would wake the observer below. */
    if (root.getAttribute(ATTR) !== value) root.setAttribute(ATTR, value);
    return value;
  }

  /*
   * Re-reads every frame until the answer has been stable for SETTLE_MS. Every event below calls
   * this rather than read(), so a change that lands after the last event is still caught. Calling
   * it again while it is running only extends the window - there is never more than one loop.
   */
  var watching = false;
  var lastValue = null;
  var lastChange = 0;

  function watch() {
    lastChange = Date.now();

    if (watching) return;
    watching = true;

    (function frame() {
      var value = read();
      var now = Date.now();

      if (value !== lastValue) {
        lastValue = value;
        lastChange = now;
      }

      if (now - lastChange > SETTLE_MS) {
        watching = false;
        return;
      }

      requestAnimationFrame(frame);
    })();
  }

  function onTransition(event) {
    if (event.propertyName !== 'background-color') return;
    if (!event.target || !event.target.closest) return;
    if (!event.target.closest(ROW)) return;
    watch();
  }

  function start() {
    read();
    watch();

    window.addEventListener('scroll', watch, { passive: true });
    window.addEventListener('scrollend', watch, { passive: true });
    window.addEventListener('resize', watch, { passive: true });
    window.addEventListener('load', watch);
    window.addEventListener('pageshow', watch);
    document.addEventListener('shopify:section:load', watch);

    /* On document, not on the row - see the note at the top. */
    document.addEventListener('transitionrun', onTransition);
    document.addEventListener('transitionstart', onTransition);
    document.addEventListener('transitionend', onTransition);

    if (window.MutationObserver) {
      new MutationObserver(watch).observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.deskonHeaderState = {
    read: read,
    watch: watch,
    debug: function () {
      var el = row();
      return {
        found: !!el,
        background: el ? getComputedStyle(el).backgroundColor : null,
        target: el ? targetBackground(el) : null,
        filled: el ? isFilled(el) : null,
        attribute: document.documentElement.getAttribute(ATTR),
        watching: watching,
      };
    },
  };
})();
